/**
 * useH3Upscale — MiniMax H3「升 2K」Hook
 *
 * ── 功能概述 ──
 * 为 H3 生成的 768P 视频节点提供「升 2K」能力。走上游
 * MiniMax-H3-Regeneration 接口，保持画面不变、只提清晰度。
 *
 * ── 与局部重绘的区别 ──
 * 没有中间编辑器：点一下就提交。所需的 source_task_id 已经存在源节点的
 * vendorTaskId 里，后端自己去项目 nodes JSON 里取，前端不用传。
 *
 * ── 交互流程 ──
 *   1. 用户选中 H3 的 768P 视频节点 → 上方出现紫色 H3UpscaleToolbar 胶囊
 *   2. 点击「升 2K」 → 本 Hook 在源节点右侧创建 loading 新节点 + 连线
 *   3. POST /api/h3-upscale → 后端异步提交并轮询
 *   4. 通过 WebSocket node-updated / node-completed 推送结果
 *
 * ── 关联文件 ──
 *   工具条:     node-parts/H3UpscaleToolbar.js
 *   后端路由:   server/routes/ai.js → POST /api/h3-upscale
 *   Provider:   server/providers/apimart-minimax-h3.js（h3_upscale + regenerate）
 *   接入点:     EditorPage.js（导入 Hook + 挂 onH3Upscale 回调）
 *   节点显示:   CustomNode.js（showH3UpscaleToolbar 条件判断）
 */
import { useCallback } from 'react';
import { useReactFlow } from 'reactflow';
import * as api from '../api';
import * as C from '../constants';

export default function useH3Upscale({ setNodes, setEdges, projectId, setAlertMsg, callbacksRef }) {
  const { getNodes } = useReactFlow();

  const handleH3Upscale = useCallback(async (nodeId) => {
    const allNodes = getNodes();
    const sourceNode = allNodes.find(n => n.id === nodeId);
    if (!sourceNode) {
      setAlertMsg?.({ type: 'error', text: '源节点不存在' });
      return;
    }
    if (!sourceNode.data?.vendorTaskId) {
      setAlertMsg?.({ type: 'error', text: '源节点缺少任务 ID，无法升 2K（请等视频完全生成后再试）' });
      return;
    }

    const targetId = `node-${Date.now()}-h3-upscale`;
    const sourcePos = sourceNode.position;
    const sourceWidth = sourceNode.data?.customSize?.width || 320;

    const cfg = callbacksRef?.current?.h3UpscaleConfigRef?.current || {};
    const toolboxName = cfg.name || 'MiniMax H3 升 2K';
    const toolboxLogo = cfg.logo || sourceNode.data?.generationModelLogo || '';

    const newNode = {
      id: targetId,
      type: 'aiNode',
      position: { x: sourcePos.x + sourceWidth + 60, y: sourcePos.y },
      selected: false,
      data: {
        type: 'video',
        title: '升 2K',
        loading: true,
        generationStartTime: Date.now(),
        generationModelName: toolboxName,
        generationModelLogo: toolboxLogo,
        statusInfo: { status: '升 2K 处理中...' },
        isDetached: true,
        isDirectUpload: true,
        customSize: sourceNode.data?.customSize || { width: 320, height: 180 },
        onUpdate: callbacksRef?.current?.onUpdate,
        onDelete: callbacksRef?.current?.onDelete,
        onGenerate: callbacksRef?.current?.onGenerate,
        onReupload: callbacksRef?.current?.onReupload,
        onReplace: callbacksRef?.current?.onReplace,
        onDetach: callbacksRef?.current?.onDetach,
        onRemoveRefFile: callbacksRef?.current?.onRemoveRefFile,
        onRemoveRefText: callbacksRef?.current?.onRemoveRefText,
        onRemoveRefAudio: callbacksRef?.current?.onRemoveRefAudio,
        onHandleDoubleClick: callbacksRef?.current?.onHandleDoubleClick,
        globalAssetsRef: callbacksRef?.current?.globalAssetsRef,
        publicAssetsRef: callbacksRef?.current?.publicAssetsRef,
      }
    };

    const newEdge = {
      id: `edge-h3-upscale-${nodeId}-${targetId}`,
      source: nodeId,
      target: targetId,
      type: 'custom',
      data: { locked: true, originalColor: C.EDGE_COLOR_AUTO, isTransmitting: true },
      style: { stroke: C.EDGE_COLOR_AUTO, strokeWidth: C.EDGE_WIDTH_NORMAL, opacity: C.OPACITY_IDLE },
    };

    setNodes(nds => [...nds, newNode]);
    setEdges(eds => [...eds, newEdge]);

    try {
      await api.apiPost('/api/h3-upscale', {
        projectId,
        sourceNodeId: nodeId,
        targetNodeId: targetId,
      });
    } catch (err) {
      setNodes(nds => nds.map(n => (
        n.id === targetId
          ? { ...n, data: { ...n.data, loading: false, error: err.message || '升 2K 请求失败' } }
          : n
      )));
      setAlertMsg?.({ type: 'error', text: `升 2K 失败: ${err.message}` });
    }
  }, [getNodes, setNodes, setEdges, projectId, setAlertMsg, callbacksRef]);

  return { handleH3Upscale };
}
